import { computed, ref, toValue, watch, type MaybeRefOrGetter } from 'vue';

export type ColumnFilterType = 'text' | 'numeric' | 'select' | 'boolean';

export interface ColumnFilterOption {
  label: string;
  value: unknown;
}

export interface ColumnField {
  field: string;
  header: string;
  /** When set, the column is filterable with a type-specific input (see `useColumnFilters`). */
  filterType?: ColumnFilterType;
  /** Field used for filtering when it differs from `field` (e.g. a nested path). */
  filterField?: string;
  /** Options for `select` filters. */
  filterOptions?: ColumnFilterOption[];
  /** Override the default match mode for this column's filter. */
  filterMatchMode?: string;
  /** Show PrimeVue's match-mode selector in the filter menu. Defaults per filter type. */
  showFilterMatchModes?: boolean;
  /** Show PrimeVue's AND/OR operator in the filter menu. Defaults per filter type. */
  showFilterOperator?: boolean;
}

interface PersistedState {
  order: string[];
  visible: string[];
}

/**
 * Manages the order and visibility of a DataTable's columns together with
 * `ColumnSelectorButton`.
 *
 * Both the column order and the visible columns are persisted per table in
 * `localStorage` under `storageKey`. Render columns with `v-for` over
 * `selectedColumns` (ordered + visible), pass `orderedColumns` and
 * `selectedColumns` to `ColumnSelectorButton`, and wire `setSelectedColumns`,
 * `setOrder` and `reset` to its events.
 */
export function useColumnSelector<T extends ColumnField>(options: {
  storageKey: string;
  columns: MaybeRefOrGetter<T[]>;
  /** Fields shown by default. Defaults to all columns when omitted. */
  defaultFields?: string[];
}) {
  const { storageKey } = options;

  const columns = computed(() => toValue(options.columns));

  const defaultFields = computed(() =>
    options.defaultFields ?? columns.value.map((column) => column.field),
  );

  function loadState(): PersistedState | null {
    const stored = localStorage.getItem(storageKey);
    if (!stored) return null;
    try {
      const parsed: unknown = JSON.parse(stored);
      if (
        parsed
        && typeof parsed === 'object'
        && Array.isArray((parsed as PersistedState).order)
        && Array.isArray((parsed as PersistedState).visible)
      ) {
        return { order: (parsed as PersistedState).order, visible: (parsed as PersistedState).visible };
      }
      // Legacy format: a plain array of visible columns (or their field names).
      if (Array.isArray(parsed) && parsed.length > 0) {
        const visible = (parsed as (ColumnField | string)[]).map((entry) =>
          typeof entry === 'string' ? entry : entry.field,
        );
        return { order: [], visible };
      }
    } catch {
      // Ignore corrupt persisted state and fall back to the defaults.
    }
    return null;
  }

  const persisted = loadState();

  const columnOrder = ref<string[]>(
    persisted?.order.length ? persisted.order : columns.value.map((column) => column.field),
  );
  const selectedFields = ref<string[]>(persisted?.visible ?? defaultFields.value);

  // All columns arranged by the persisted order; unknown fields are appended
  // and removed fields are dropped so the order always matches the definitions.
  const orderedColumns = computed<T[]>(() => {
    const byField = new Map(columns.value.map((column) => [column.field, column]));
    const ordered = columnOrder.value
      .map((field) => byField.get(field))
      .filter((column): column is T => !!column);
    const seen = new Set(ordered.map((column) => column.field));
    for (const column of columns.value) {
      if (!seen.has(column.field)) ordered.push(column);
    }
    return ordered;
  });

  const selectedColumns = computed<T[]>(() =>
    orderedColumns.value.filter((column) => selectedFields.value.includes(column.field)),
  );

  function isVisible(field: string): boolean {
    return selectedFields.value.includes(field);
  }

  function setSelectedColumns(value: ColumnField[]) {
    // Keep the current column order regardless of the order columns were toggled in.
    selectedFields.value = orderedColumns.value
      .filter((column) => value.some((selected) => selected.field === column.field))
      .map((column) => column.field);
  }

  function setOrder(value: ColumnField[]) {
    columnOrder.value = value.map((column) => column.field);
  }

  function reset() {
    columnOrder.value = columns.value.map((column) => column.field);
    selectedFields.value = defaultFields.value;
    localStorage.removeItem(storageKey);
  }

  watch(
    [orderedColumns, selectedFields],
    () => {
      const state: PersistedState = {
        order: orderedColumns.value.map((column) => column.field),
        visible: selectedFields.value,
      };
      localStorage.setItem(storageKey, JSON.stringify(state));
    },
    { deep: true },
  );

  return {
    columns,
    orderedColumns,
    selectedColumns,
    selectedFields,
    columnOrder,
    isVisible,
    setSelectedColumns,
    setOrder,
    reset,
  };
}
